import { enqueue, eventId, queueSize, removeIds, takeBatch } from './activityQueue';
import { ActivityEnrichment, ActivityEvent, PageContext, WebActivityIdentity } from './types';
import WebActivityClient from './webActivityClient';

/**
 * IMBEE-8954 — the beacon's firing + batching engine (TDS §6f-vi/vii).
 *
 * Flush triggers, and why each exists:
 *   F1  page_view enqueued      debounce 2s   coalesces rapid SPA route changes into one request
 *   F2  visibilitychange/pagehide  immediate  the ONLY reliable delivery point before the document
 *                                             dies; carries the dwell close-out
 *   F3  boot, queue non-empty   1s            drains what the PREVIOUS page could not finish
 *   F4  heartbeat while visible 30s           a ten-minute read otherwise reports nothing
 *   F5  after a success, more remain  0       drains a >BATCH_SIZE backlog
 *   F6  after a failure         2→30s backoff network blips
 *
 * F2 uses `visibilitychange`, NOT `beforeunload`/`unload`: those are unreliable on mobile Safari and
 * skipped entirely for bfcache-eligible pages. `pagehide` is the belt to its braces — both firing is
 * harmless, guarded by `inFlight` and deduped server-side.
 */

export const DEBOUNCE_MS = 2000;
export const BOOT_DELAY_MS = 1000;
export const HEARTBEAT_MS = 30000;
/** ~4KB per request at this size — well inside `keepalive`'s 64KB cap. */
export const BATCH_SIZE = 20;
export const BACKOFF_START_MS = 2000;
export const BACKOFF_MAX_MS = 30000;

/**
 * How long campaign evaluation will wait for the first beacon before proceeding without it. Long
 * enough for a normal round trip, short enough that a dead ingest delays the greeting rather than
 * suppressing it.
 */
export const FIRST_BEACON_MAX_WAIT_MS = 3000;

type Timer = ReturnType<typeof setTimeout>;

class WebActivityController {
  private client: WebActivityClient;

  private enabled = false;

  private inFlight = false;

  private backoffMs = 0;

  private debounceTimer: Timer | null = null;

  private heartbeatTimer: Timer | null = null;

  private bootTimer: Timer | null = null;

  /** The page currently being dwelled on. `enteredAt` is stamped once and never changes. */
  private openEvent: { url: string; enteredAt: string; title?: string } | null = null;

  /** Wall-clock accumulator, paused while hidden so an overnight tab does not report 8h of reading. */
  private dwellStartedAt: number | null = null;

  private dwellAccumulatedMs = 0;

  /** Enrichment is `$setOnInsert`/stable server-side, so it rides only the first flush per page. */
  private enrichmentPending: ActivityEnrichment | null = null;

  private currentPage: string | undefined;

  /**
   * The page context that arrived BEFORE `GET /widget` resolved, held so enabling can replay it.
   *
   * On a non-SPA page the initial `IMBEE_PAGE_CONTEXT` is the ONLY one the visit will ever produce
   * (`pageMessageChannel`'s own comment), and it is posted as soon as the widget announces its
   * channel — which is several awaited calls before the entitlement is known. Dropping it therefore
   * loses the entire visit, not merely its first event. `CampaignController` solves the same race
   * the same way; this mirrors it deliberately.
   */
  private pendingContext: { context: PageContext; enrichment?: ActivityEnrichment } | null = null;

  /**
   * Resolves once the FIRST beacon of this page load has actually been POSTed (or once we know one
   * never will be). Campaign evaluation awaits it — see `whenFirstBeaconSettled`.
   */
  private firstBeaconSettled: Promise<void>;

  private resolveFirstBeacon: (() => void) | null = null;

  private identity: WebActivityIdentity;

  constructor(client: WebActivityClient, identity: WebActivityIdentity) {
    this.firstBeaconSettled = new Promise<void>((resolve) => {
      this.resolveFirstBeacon = resolve;
    });
    this.client = client;
    this.identity = identity;
  }

  /**
   * Gated on the tenant's Website Tracking entitlement, resolved from `GET /widget`. Order-
   * independent by design: a page context that arrived before the flag resolved is not lost, it is
   * simply held in the queue until enabling releases it.
   */
  setEnabled(enabled: boolean): void {
    const wasEnabled = this.enabled;
    this.enabled = enabled;
    if (!enabled) {
      this.clearTimers();
      // No beacon will ever be sent, so release the gate now — a tenant without website tracking
      // must still get campaigns rather than sitting out the timeout on every page.
      this.settleFirstBeacon();
      return;
    }
    if (!wasEnabled) {
      // Replay first, THEN drain: the replay is what puts this visit's page view in the queue.
      const held = this.pendingContext;
      this.pendingContext = null;
      if (held) this.onPageContext(held.context, held.enrichment);
      this.scheduleBoot();
    }
  }

  /** F1 — a page load or SPA route change. Closes out the previous page, opens the new one. */
  onPageContext(context: PageContext, enrichment?: ActivityEnrichment): void {
    if (!this.enabled) {
      // Held, not dropped — see `pendingContext`. Last-one-wins: if several arrive before the
      // entitlement resolves, the most recent is the page the visitor is actually on.
      this.pendingContext = { context, enrichment };
      return;
    }
    // SAME PAGE ALREADY OPEN -> keep the existing event; do NOT mint a second one.
    //
    // `enteredAt` is half the server's natural key, so opening a new event for a page the visitor
    // never left produces a SECOND timeline row for one visit. That happens routinely: the loader
    // queues IMBEE_PAGE_CONTEXT and re-posts it once the widget announces its channel, and the
    // entitlement replay above can deliver the same context again. The observed result was two
    // rows ~10s apart for a single page load.
    //
    // A genuine re-visit (A -> B -> A) still opens a new event, because leaving A closed it and
    // `openEvent.url` is B by the time A comes back.
    if (this.openEvent && this.openEvent.url === context.url) {
      this.currentPage = context.url;
      if (this.dwellStartedAt === null) this.dwellStartedAt = Date.now();
      return;
    }

    this.closeOpenEvent();

    this.currentPage = context.url;
    this.openEvent = { url: context.url, enteredAt: new Date().toISOString() };
    this.dwellStartedAt = Date.now();
    this.dwellAccumulatedMs = 0;

    if (enrichment && !context.isRouteChange) this.enrichmentPending = enrichment;

    enqueue({ url: this.openEvent.url, enteredAt: this.openEvent.enteredAt, eventType: 'page_view' });
    this.scheduleDebouncedFlush();
    this.startHeartbeat();
  }

  /** A non-page event (a CTA click, etc). `eventType` is part of the key, so it never collides. */
  trackEvent(event: ActivityEvent): void {
    if (!this.enabled) return;
    enqueue(event);
    this.scheduleDebouncedFlush();
  }

  /** F2 — the page is going away. Stamp dwell and go NOW; no debounce. */
  onHidden(): void {
    if (!this.enabled) return;
    this.pauseDwell();
    this.closeOpenEvent({ keepOpen: true });
    this.cancelDebounce();
    // `force` — F2 must NOT be dropped by the in-flight guard. If a debounced flush is still open
    // when the visitor navigates away, skipping this send strands the dwell close-out in
    // localStorage; on a single-page visit (they left the site) nothing ever drains it. A
    // concurrent send is safe: the server upserts on {sessionId, enteredAt, url, eventType}, so the
    // overlap collapses into one row rather than double-counting.
    void this.flush({ force: true });
  }

  /** The visitor came back to the tab — resume the dwell clock. */
  onVisible(): void {
    if (!this.enabled) return;
    if (this.openEvent && this.dwellStartedAt === null) this.dwellStartedAt = Date.now();
  }

  /**
   * Re-enqueues the open page with its dwell so far. `keepOpen` is the F2/F4 case: the visitor has
   * not actually left the page, so the event stays open and will be re-sent with a larger figure.
   */
  private closeOpenEvent(opts: { keepOpen?: boolean } = {}): void {
    if (!this.openEvent) return;
    const timeOnPageMs = this.dwellMs();
    enqueue({
      url: this.openEvent.url,
      enteredAt: this.openEvent.enteredAt,
      eventType: 'page_view',
      ...(this.openEvent.title ? { title: this.openEvent.title } : {}),
      ...(timeOnPageMs > 0 ? { timeOnPageMs } : {}),
    });
    if (!opts.keepOpen) {
      this.openEvent = null;
      this.dwellStartedAt = null;
      this.dwellAccumulatedMs = 0;
    }
  }

  private dwellMs(): number {
    const live = this.dwellStartedAt === null ? 0 : Date.now() - this.dwellStartedAt;
    return this.dwellAccumulatedMs + live;
  }

  private pauseDwell(): void {
    if (this.dwellStartedAt === null) return;
    this.dwellAccumulatedMs += Date.now() - this.dwellStartedAt;
    this.dwellStartedAt = null;
  }

  private scheduleDebouncedFlush(): void {
    this.cancelDebounce();
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.flush();
    }, DEBOUNCE_MS);
  }

  private cancelDebounce(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
  }

  /** F3 */
  private scheduleBoot(): void {
    if (this.bootTimer || queueSize() === 0) return;
    this.bootTimer = setTimeout(() => {
      this.bootTimer = null;
      void this.flush();
    }, BOOT_DELAY_MS);
  }

  /** F4 */
  private startHeartbeat(): void {
    if (this.heartbeatTimer) return;
    this.heartbeatTimer = setInterval(() => {
      if (!this.openEvent || this.dwellStartedAt === null) return;
      this.closeOpenEvent({ keepOpen: true });
      void this.flush();
    }, HEARTBEAT_MS);
  }

  private clearTimers(): void {
    this.cancelDebounce();
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    if (this.bootTimer) clearTimeout(this.bootTimer);
    this.heartbeatTimer = null;
    this.bootTimer = null;
  }

  private settleFirstBeacon(): void {
    if (!this.resolveFirstBeacon) return;
    const resolve = this.resolveFirstBeacon;
    this.resolveFirstBeacon = null;
    resolve();
  }

  /**
   * A campaign whose conditions read the visitor's activity (dwell time, page count) CANNOT be
   * evaluated before that activity exists. The widget used to fire evaluate and the first beacon
   * concurrently, and evaluate always won — so a genuinely-new visitor was evaluated against an
   * empty record and matched nothing. The teaser then appeared only on a RELOAD, once the previous
   * visit's beacon was already stored, which is not something a real visitor does.
   *
   * Resolves when the first beacon has been sent, when tracking is off (nothing will ever be sent),
   * or after `FIRST_BEACON_MAX_WAIT_MS` — a beacon that fails must delay a campaign, never cancel
   * it, since `/events` reports every outcome as 204 and cannot be waited on for success.
   */
  whenFirstBeaconSettled(): Promise<void> {
    return Promise.race([
      this.firstBeaconSettled,
      new Promise<void>((resolve) => setTimeout(resolve, FIRST_BEACON_MAX_WAIT_MS)),
    ]);
  }

  async flush(opts: { force?: boolean } = {}): Promise<void> {
    if (!this.enabled) return;
    if (this.inFlight && !opts.force) return;

    const batch = takeBatch(BATCH_SIZE);
    if (batch.length === 0) {
      this.settleFirstBeacon();
      return;
    }

    // Read live, never snapshotted: the visitor token can change after import (session creation).
    const visitorToken = this.identity.getVisitorToken();
    if (!visitorToken) {
      this.settleFirstBeacon();
      return;
    }

    const heldFlight = this.inFlight;
    this.inFlight = true;
    try {
      const enrichment = this.enrichmentPending ?? {};
      const ok = await this.client.send({
        visitorToken,
        ...(this.currentPage ? { currentPage: this.currentPage } : {}),
        ...enrichment,
        events: batch.map(({ id, ...event }) => event),
      });

      if (ok) {
        removeIds(batch.map((e) => e.id));
        this.enrichmentPending = null;
        this.backoffMs = 0;
        // F5 — drain the rest of a backlog rather than waiting for the next trigger.
        if (queueSize() > 0) {
          this.inFlight = heldFlight;
          void this.flush();
          return;
        }
      } else {
        this.scheduleRetry(); // F6
      }
    } finally {
      // The first beacon has now been ATTEMPTED. Settled here rather than only on `ok`, because
      // a failed send must DELAY a campaign, never suppress it.
      this.settleFirstBeacon();
      // A forced F2 send that overlapped an open flush must not clear the other one's flag.
      this.inFlight = heldFlight;
    }
  }

  private scheduleRetry(): void {
    this.backoffMs = this.backoffMs === 0 ? BACKOFF_START_MS : Math.min(this.backoffMs * 2, BACKOFF_MAX_MS);
    this.cancelDebounce();
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.flush();
    }, this.backoffMs);
  }

  /** Test seam — the id form the server's natural key mirrors. */
  static idOf = eventId;
}

export default WebActivityController;
